import React, { useState, useEffect } from 'react';
import {
    FileText, Download, Filter, BarChart2, Users, BookOpen,
    Eye, CheckCircle, ArrowLeft, RefreshCw, TrendingUp,
    Award, AlertTriangle, FileSpreadsheet, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../utils/api';
import SummaryCard from '../../components/Common/SummaryCard';

const REPORT_TYPES = [
    { id: 'performance', label: 'Performance Analysis', icon: BarChart2, desc: 'Grades & assessment scores' },
    { id: 'attendance', label: 'Attendance Records', icon: Users, desc: 'Participation statistics' },
    { id: 'courses', label: 'Course Completion', icon: BookOpen, desc: 'Module & curriculum tracking' },
];

const StatusBadge = ({ status }) => {
    const map = {
        Excellent: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/30',
        Good:      'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-[#1a3884]/30 dark:text-blue-300 dark:border-[#1a3884]/40',
        Average:   'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/30',
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold ${map[status] || map.Average}`}>
            {status}
        </span>
    );
};

const MiniBar = ({ value, max = 100 }) => {
    const pct = Math.min(100, Math.max(0, value));
    const color = pct >= 90 ? 'bg-emerald-500' : pct >= 75 ? 'bg-[#4f7dff]' : pct >= 60 ? 'bg-amber-400' : 'bg-red-500';
    return (
        <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-800 dark:text-white w-10 text-right">{value}%</span>
            <div className="w-20 h-1.5 bg-gray-100 dark:bg-[#001f40] rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const Reports = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [reportType, setReportType] = useState('performance');
    const [dateRange, setDateRange] = useState('month');
    const [department, setDepartment] = useState('all');
    const [allStudents, setAllStudents] = useState([]);
    const [tablePage, setTablePage] = useState(1);
    const rowsPerPage = 8;

    useEffect(() => {
        if (user?.college) fetchReportData();
    }, [user]);

    const previewData = React.useMemo(() => {
        return allStudents.filter(s => {
            if (department !== 'all') {
                return s.dept.trim().toLowerCase() === department.trim().toLowerCase();
            }
            return true;
        });
    }, [allStudents, department]);

    const uniqueDepartments = React.useMemo(() => {
        const depts = Array.from(new Set(allStudents.map(s => s.dept.trim())))
            .filter(Boolean)
            .sort();
        return depts;
    }, [allStudents]);

    useEffect(() => {
        setTablePage(1);
    }, [reportType, dateRange, department, previewData.length]);

    const fetchReportData = async () => {
        try {
            setFetching(true);
            const collegeId = typeof user.college === 'object' ? user.college._id : user.college;
            const { data } = await api.get(`/analytics/students/${collegeId}`);
            if (data.success) {
                const transformed = data.data
                    .map(s => ({
                        name: s.fullName || s.name || 'Unknown Student',
                        dept: s.department || s.dept || 'General',
                        attendance: s.completionRate !== undefined ? s.completionRate : (s.attendance || 0),
                        score: s.score || 0,
                        status: s.academicStatus || s.status || 'Average'
                    }))
                    .filter(s => {
                        if (department === 'all') return true;
                        const sd = s.dept.toLowerCase();
                        if (department === 'cs') return sd.includes('computer science') || sd === 'cs';
                        if (department === 'eng') return sd.includes('engineering') || sd === 'eng';
                        if (department === 'math') return sd.includes('math') || sd === 'math';
                        return sd === department.toLowerCase();
                    });
                setAllStudents(transformed);
            }
        } catch (err) {
            console.error('Failed to fetch report data:', err);
        } finally {
            setFetching(false);
        }
    };

    const avgScore = previewData.length
        ? (previewData.reduce((a, r) => a + r.score, 0) / previewData.length).toFixed(1)
        : 0;
    const avgAttendance = previewData.length
        ? (previewData.reduce((a, r) => a + r.attendance, 0) / previewData.length).toFixed(1)
        : 0;
    const excellentCount = previewData.filter(r => r.status === 'Excellent').length;
    const tablePageCount = Math.max(1, Math.ceil(previewData.length / rowsPerPage));
    const safeTablePage = Math.min(tablePage, tablePageCount);
    const tableStartIndex = (safeTablePage - 1) * rowsPerPage;
    const paginatedPreviewData = previewData.slice(tableStartIndex, tableStartIndex + rowsPerPage);

    const generatePDF = () => {
        if (!previewData.length) { toast.error('No data to export'); return; }
        setLoading(true);
        try {
            const doc = new jsPDF();
            const collegeName = user?.college?.collegeName || user?.collegeName || 'Institute';

            // Brand header bar
            doc.setFillColor(26, 56, 132);
            doc.rect(0, 0, 210, 42, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text(`${collegeName}`, 14, 18);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text(`${REPORT_TYPES.find(r => r.id === reportType)?.label || 'Report'}`, 14, 28);
            doc.setFontSize(9);
            doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy, HH:mm')}`, 14, 38);
            doc.text(`Period: ${dateRange === 'week' ? 'Last 7 Days' : dateRange === 'month' ? 'Last 30 Days' : dateRange === 'quarter' ? 'Last Quarter' : 'Academic Year'}`, 140, 38);

            // Summary band
            doc.setFillColor(243, 244, 246);
            doc.rect(0, 42, 210, 22, 'F');
            doc.setTextColor(26, 56, 132);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Students: ${previewData.length}`, 14, 52);
            doc.text(`Avg Score: ${avgScore}%`, 75, 52);
            doc.text(`Avg Attendance: ${avgAttendance}%`, 130, 52);
            doc.text(`Excellent: ${excellentCount}`, 14, 60);

            // Table
            doc.autoTable({
                head: [['#', 'Student Name', 'Department', 'Attendance', 'Avg Score', 'Status']],
                body: previewData.map((row, i) => [i + 1, row.name, row.dept, `${row.attendance}%`, `${row.score}%`, row.status]),
                startY: 70,
                theme: 'grid',
                headStyles: { fillColor: [26, 56, 132], textColor: 255, fontStyle: 'bold', fontSize: 9 },
                bodyStyles: { fontSize: 9 },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                columnStyles: {
                    0: { cellWidth: 10 },
                    5: { fontStyle: 'bold' }
                },
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 5) {
                        const v = data.cell.raw;
                        data.cell.styles.textColor = v === 'Excellent' ? [5, 150, 105] : v === 'Good' ? [26, 56, 132] : [180, 120, 0];
                    }
                }
            });

            // Footer
            const pages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pages; i++) {
                doc.setPage(i);
                doc.setFillColor(26, 56, 132);
                doc.rect(0, 286, 210, 12, 'F');
                doc.setFontSize(8);
                doc.setTextColor(255, 255, 255);
                doc.text('Confidential — Internal Use Only', 14, 293);
                doc.text(`Page ${i} of ${pages}`, 196, 293, { align: 'right' });
            }

            doc.save(`${collegeName}-${reportType}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
            toast.success('PDF downloaded successfully');
        } catch (err) {
            console.error(err);
            toast.error('Failed to generate PDF');
        } finally {
            setLoading(false);
        }
    };

    const generateExcel = () => {
        if (!previewData.length) { toast.error('No data to export'); return; }
        setLoading(true);
        try {
            const wb = XLSX.utils.book_new();
            const rows = previewData.map((r, i) => ({
                '#': i + 1,
                'Student Name': r.name,
                'Department': r.dept,
                'Attendance (%)': r.attendance,
                'Avg Score (%)': r.score,
                'Status': r.status,
            }));
            const ws = XLSX.utils.json_to_sheet(rows);
            // Column widths
            ws['!cols'] = [{ wch: 4 }, { wch: 28 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];
            XLSX.utils.book_append_sheet(wb, ws, 'Report');
            const collegeName = user?.college?.collegeName || 'Institute';
            XLSX.writeFile(wb, `${collegeName}-${reportType}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
            toast.success('Excel downloaded successfully');
        } catch (err) {
            console.error(err);
            toast.error('Failed to generate Excel');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="space-y-0.5 text-left">
                        <h1 className="text-[22px] font-black text-[var(--placement-primary)] tracking-tight">Reports & Export</h1>
                        <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
                            <FileText className="w-4 h-4 text-[var(--placement-primary)] opacity-60" />
                            Generate, preview and download institutional reports
                        </div>
                    </div>
                </div>
                <button
                    onClick={fetchReportData}
                    disabled={fetching}
                    className="border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#001f40] rounded-xl px-4 py-2.5 text-sm font-bold flex items-center gap-2 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:ring-offset-2"
                >
                    <RefreshCw className={`w-4 h-4 ${fetching ? 'animate-spin' : ''}`} />
                    Refresh Data
                </button>
            </div>
            {/* Summary stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <SummaryCard
                    title="Total Students"
                    value={previewData.length}
                    icon={Users}
                    layout="row"
                />
                <SummaryCard
                    title="Avg Score"
                    value={`${avgScore}%`}
                    icon={Award}
                    layout="row"
                />
                <SummaryCard
                    title="Avg Attendance"
                    value={`${avgAttendance}%`}
                    icon={TrendingUp}
                    layout="row"
                />
                <SummaryCard
                    title="Excellent"
                    value={excellentCount}
                    icon={CheckCircle}
                    layout="row"
                />
            </div>

            {/* Report type */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-150 dark:border-[#1a3884]/15 flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[var(--placement-primary)]" />
                    <h3 className="text-[13px] font-black text-gray-700 dark:text-gray-250 uppercase tracking-wide">Report Type</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {REPORT_TYPES.map(type => {
                        const active = reportType === type.id;
                        return (
                            <button
                                key={type.id}
                                onClick={() => setReportType(type.id)}
                                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left
                                    ${active
                                        ? 'border-[var(--placement-primary)] bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)] text-[var(--placement-primary)] dark:text-[#4f7dff]'
                                        : 'border-gray-100 dark:border-[#1a3884]/20 text-gray-700 dark:text-gray-305 hover:bg-gray-50 dark:hover:bg-[#001f40]'}`}
                            >
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${active ? 'bg-[var(--placement-primary)] text-white' : 'bg-gray-100 dark:bg-[#001f40] text-gray-400 dark:text-gray-500'}`}>
                                    <type.icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-[13px] font-bold ${active ? 'text-[var(--placement-primary)] dark:text-[#4f7dff]' : 'text-gray-700 dark:text-gray-250'}`}>{type.label}</p>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{type.desc}</p>
                                </div>
                                {active && <CheckCircle className="w-4 h-4 text-[var(--placement-primary)] dark:text-[#4f7dff] shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            </div>
            {/* Data table */}
            <div className="bg-white dark:bg-[#002147] rounded-xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm overflow-hidden flex-1 [&>div]:p-0">                {/* Table header */}
                <div className="flex items-center justify-between px-6 pt-[44px] pb-[24px] border-b border-gray-100 dark:border-[#1a3884]/20 bg-white dark:bg-[#002147]">
                    <div className="flex items-center gap-3">
                        <Eye className="w-[18px] h-[18px] text-[var(--placement-primary)] dark:text-[#4f7dff] shrink-0" />
                        <div className="flex items-center gap-2">
                            <h3 className="text-[13px] font-black text-gray-700 dark:text-gray-250 leading-none">Live Preview</h3>
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                            <span className="text-[11px] text-gray-400 leading-none">{REPORT_TYPES.find(r => r.id === reportType)?.label}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-250 dark:border-emerald-800/30 shrink-0">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                        <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 leading-none">Ready to Export</span>
                    </div>
                </div>

                {/* Filters */}
                <div className="px-6 pt-6 pb-8 border-b border-gray-100 dark:border-[#1a3884]/20 bg-gray-50/50 dark:bg-[#001f40]/25">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3.5 text-left">
                            <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Time Period</label>
                            <select
                                value={dateRange}
                                onChange={e => setDateRange(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px] font-bold outline-none"
                            >
                                <option value="week">Last 7 Days</option>
                                <option value="month">Last 30 Days</option>
                                <option value="quarter">Last Quarter</option>
                                <option value="year">Academic Year</option>
                            </select>
                        </div>
                        <div className="space-y-3.5 text-left">
                            <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Department</label>
                            <select
                                value={department}
                                onChange={e => setDepartment(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px] font-bold outline-none"
                            >
                                <option value="all">All Departments</option>
                                {uniqueDepartments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {fetching ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                        <RefreshCw className="w-8 h-8 text-[var(--placement-primary)] dark:text-[#4f7dff] animate-spin" />
                        <p className="text-[13px] text-gray-400 font-medium">Loading report data...</p>
                    </div>
                ) : previewData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                        <AlertTriangle className="w-10 h-10 text-gray-200 dark:text-[#1a3884]/40" />
                        <p className="text-[13px] text-gray-400 font-medium">No students found for the selected filters</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] table-fixed text-sm">
                            <colgroup>
                                <col className="w-[5%]" />
                                <col className="w-[21%]" />
                                <col className="w-[28%]" />
                                <col className="w-[17%]" />
                                <col className="w-[17%]" />
                                <col className="w-[12%]" />
                            </colgroup>
                            <thead>
                                <tr className="bg-gray-50/75 dark:bg-[#001f40]/40 border-b border-gray-200 dark:border-[#1a3884]/30">
                                    {['#', 'Student', 'Department', 'Attendance', 'Score', 'Status'].map(h => (
                                        <th key={h} className="px-6 py-[18px] text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-[#1a3884]/20">
                                {paginatedPreviewData.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-[#1a3884]/10 transition-colors">
                                        <td className="px-6 py-5 text-xs font-bold text-gray-400 align-middle">{tableStartIndex + idx + 1}</td>
                                        <td className="px-6 py-5 min-w-0 align-middle">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-full flex items-center justify-center text-white font-bold shadow-sm overflow-hidden shrink-0 text-xs">
                                                    {row.name.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="text-sm font-semibold text-gray-800 dark:text-white truncate" title={row.name}>{row.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-xs text-gray-550 dark:text-gray-400 truncate align-middle" title={row.dept}>{row.dept}</td>
                                        <td className="px-6 py-5 align-middle"><MiniBar value={row.attendance} /></td>
                                        <td className="px-6 py-5 align-middle"><MiniBar value={row.score} /></td>
                                        <td className="px-6 py-5 align-middle"><StatusBadge status={row.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Table footer */}
                {previewData.length > 0 && (
                    <div className="px-6 py-[24px] border-t border-gray-100 dark:border-[#1a3884]/20 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs bg-slate-50/50 dark:bg-[#001f40]/20">
                        <div className="text-gray-450 dark:text-gray-400 min-w-[200px] text-left">
                            Showing {tableStartIndex + 1}-{Math.min(tableStartIndex + rowsPerPage, previewData.length)} of {previewData.length} students
                        </div>
                        <div className="text-gray-450 dark:text-gray-400 font-medium text-center flex-1">
                            <span className="font-bold text-gray-600 dark:text-gray-300">{excellentCount}</span> excellent <span className="mx-1.5 opacity-40">•</span> <span className="font-bold text-gray-600 dark:text-gray-300">{previewData.filter(r => r.status === 'Good').length}</span> good <span className="mx-1.5 opacity-40">•</span> <span className="font-bold text-gray-600 dark:text-gray-300">{previewData.filter(r => r.status === 'Average').length}</span> average
                        </div>
                        <div className="flex items-center justify-end gap-3 min-w-[200px]">
                            <button
                                onClick={() => setTablePage(page => Math.max(1, page - 1))}
                                disabled={safeTablePage === 1}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-gray-200 dark:border-[#1a3884]/30 rounded-xl hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-600 dark:text-gray-300 transition-all disabled:opacity-40 bg-white dark:bg-[#001f40]"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" />
                                Prev
                            </button>
                            <span className="font-bold text-gray-600 dark:text-gray-300">
                                Page {safeTablePage} of {tablePageCount}
                            </span>
                            <button
                                onClick={() => setTablePage(page => Math.min(tablePageCount, page + 1))}
                                disabled={safeTablePage === tablePageCount}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-gray-200 dark:border-[#1a3884]/30 rounded-xl hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-600 dark:text-gray-300 transition-all disabled:opacity-40 bg-white dark:bg-[#001f40]"
                            >
                                Next
                                <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Export actions */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/20 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-150 dark:border-[#1a3884]/15">
                    <h3 className="text-[13px] font-black text-gray-700 dark:text-gray-200 uppercase tracking-wide">Download</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                        onClick={generatePDF}
                        disabled={loading || !previewData.length}
                        className="w-full flex items-center gap-3 px-4 py-2.5 border border-[var(--placement-primary)] dark:border-[#4f7dff]/60 hover:bg-[var(--placement-primary-light)] text-[var(--placement-primary)] dark:text-[#4f7dff] bg-transparent rounded-xl font-bold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed text-left justify-start shadow-sm"
                    >
                        <FileText className="w-4 h-4 shrink-0 text-[var(--placement-primary)] dark:text-[#4f7dff]" />
                        <span className="flex-1 text-left">Download PDF Report</span>
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin text-[var(--placement-primary)] dark:text-[#4f7dff]" /> : <Download className="w-4 h-4 text-[var(--placement-primary)] dark:text-[#4f7dff]" />}
                    </button>
                    <button
                        onClick={generateExcel}
                        disabled={loading || !previewData.length}
                        className="w-full flex items-center gap-3 px-4 py-2.5 border border-[var(--placement-primary)] dark:border-[#4f7dff]/60 hover:bg-[var(--placement-primary-light)] text-[var(--placement-primary)] dark:text-[#4f7dff] bg-transparent rounded-xl font-bold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed text-left justify-start shadow-sm"
                    >
                        <FileSpreadsheet className="w-4 h-4 shrink-0 text-[var(--placement-primary)] dark:text-[#4f7dff]" />
                        <span className="flex-1 text-left">Download Excel Sheet</span>
                        <Download className="w-4 h-4 text-[var(--placement-primary)] dark:text-[#4f7dff]" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Reports;
